// Inline SVG charts, no dependency. These exist because a curve rendered as a
// table of numbers makes the reader do the plotting in their head, and the two
// things they most need to see here — which quarters lost money, and how late
// the return arrives — are both shapes rather than values.

const fmtPct = (v, d = 1) => `${v > 0 ? '+' : ''}${v.toFixed(d)}%`;

/**
 * Signed bars on a shared zero line. Losing periods sit below it and take the
 * negative colour, so a bad patch is visible before any number is read.
 */
export function BarChart({ data, height = 132, label = 'Returns by period', valueLabel = fmtPct }) {
  if (!data?.length) return null;

  const values = data.map((d) => d.value);
  const max = Math.max(...values, 0);
  const min = Math.min(...values, 0);
  const span = max - min || 1;
  const zeroY = (max / span) * height;

  const gap = 3;
  const bandW = 100 / data.length;

  return (
    <figure className="chart" aria-label={label}>
      <svg viewBox={`0 0 100 ${height}`} preserveAspectRatio="none" className="chart-svg" style={{ height }}>
        <line x1="0" y1={zeroY} x2="100" y2={zeroY} className="chart-zero" vectorEffect="non-scaling-stroke" />
        {data.map((d, i) => {
          const h = (Math.abs(d.value) / span) * height;
          const y = d.value >= 0 ? zeroY - h : zeroY;
          return (
            <rect
              key={d.label}
              x={i * bandW + gap / 2}
              y={y}
              width={bandW - gap}
              height={Math.max(h, 0.8)}
              className={d.value >= 0 ? 'chart-bar pos' : 'chart-bar neg'}
            >
              <title>{`${d.label}: ${valueLabel(d.value)}${d.note ? ` · ${d.note}` : ''}`}</title>
            </rect>
          );
        })}
      </svg>
      <figcaption className="chart-axis">
        {data.map((d) => (
          <span key={d.label} style={{ width: `${bandW}%` }}>
            {d.short ?? d.label}
          </span>
        ))}
      </figcaption>
    </figure>
  );
}

/**
 * Two series per period on one shared scale.
 *
 * A single bar showing only the gap over a benchmark is easy to misread: the
 * lengths get normalised to whichever period was best, so a modest number can
 * fill half the track, and the benchmark it is being measured against is never
 * on screen. Drawing both, against the same axis, makes the comparison the
 * reader is actually being asked to make.
 */
export function CompareBars({ data, aLabel = 'This strategy', bLabel = 'Index', valueLabel = fmtPct }) {
  if (!data?.length) return null;

  const values = data.flatMap((d) => [d.a, d.b]);
  const max = Math.max(...values, 0);
  const min = Math.min(...values, 0);
  const span = max - min || 1;
  const zero = ((0 - min) / span) * 100;

  const geom = (v) => {
    const at = ((v - min) / span) * 100;
    return { left: Math.min(zero, at), width: Math.max(Math.abs(at - zero), 0.6) };
  };

  return (
    <ul className="cbars" aria-label={`${aLabel} against ${bLabel} by period`}>
      {data.map((d) => (
        <li key={d.label} className="cbar">
          <div className="cbar-period">{d.label}</div>
          {[
            ['a', aLabel, d.a],
            ['b', bLabel, d.b],
          ].map(([cls, name, v]) => {
            const g = geom(v);
            return (
              <div className={`cbar-row ${cls} ${v < 0 ? 'neg' : ''}`} key={cls}>
                <span className="cbar-name">{name}</span>
                <span className="cbar-track">
                  {min < 0 && <i className="cbar-zero" style={{ left: `${zero}%` }} />}
                  <i className="cbar-fill" style={{ left: `${g.left}%`, width: `${g.width}%` }} />
                </span>
                <span className="cbar-value">{valueLabel(v)}</span>
              </div>
            );
          })}
          {d.note && <div className="cbar-note">{d.note}</div>}
        </li>
      ))}
    </ul>
  );
}

/**
 * A score out of 100 drawn as an arc. A bare numeral says nothing about where
 * it sits on the scale; the sweep does that before the number is read.
 */
export function ScoreRing({ value, max = 100, size = 56, tone = '' }) {
  const stroke = 5;
  const r = (size - stroke) / 2 - 1;
  const circumference = 2 * Math.PI * r;
  const filled = Math.max(0, Math.min(1, (value ?? 0) / max));
  const mid = size / 2;

  return (
    <svg
      className={`ring ${tone}`}
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      role="img"
      aria-label={`Score ${value} out of ${max}`}
    >
      <circle className="ring-track" cx={mid} cy={mid} r={r} strokeWidth={stroke} fill="none" />
      <circle
        className="ring-arc"
        cx={mid}
        cy={mid}
        r={r}
        strokeWidth={stroke}
        fill="none"
        strokeLinecap="round"
        strokeDasharray={`${circumference * filled} ${circumference}`}
        transform={`rotate(-90 ${mid} ${mid})`}
      />
      <text className="ring-num" x="50%" y="50%" dominantBaseline="central" textAnchor="middle">
        {value}
      </text>
    </svg>
  );
}

/**
 * A screen is a funnel, and the interesting part is where the drop-off happens.
 * Seven equal stat tiles hid that; proportional bars make it obvious which
 * stage did the filtering.
 */
export function Funnel({ steps }) {
  if (!steps?.length) return null;
  const top = Math.max(...steps.map((s) => s.value), 1);

  return (
    <ol className="funnel" aria-label="Screening funnel">
      {steps.map((s, i) => {
        const prev = i > 0 ? steps[i - 1].value : null;
        const dropped = prev !== null ? prev - s.value : null;
        return (
          <li key={s.label} className={`funnel-step ${s.tone ?? ''}`}>
            <div className="funnel-head">
              <span className="funnel-label">{s.label}</span>
              <span className="funnel-value">{s.value?.toLocaleString('en-IN') ?? '—'}</span>
            </div>
            <div className="funnel-track">
              <div className="funnel-fill" style={{ width: `${Math.max((s.value / top) * 100, 1)}%` }} />
            </div>
            <div className="funnel-note">
              {dropped > 0 ? `${dropped.toLocaleString('en-IN')} removed here` : s.note ?? ''}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

/**
 * A single line with the area beneath it filled. Used for the maturity curve,
 * where the point is that the line stays flat for weeks before it climbs.
 */
export function LineChart({ data, height = 150, label = 'Series', valueLabel = fmtPct, markers = [] }) {
  if (!data?.length) return null;

  const ys = data.map((d) => d.y);
  const max = Math.max(...ys, 0);
  const min = Math.min(...ys, 0);
  const span = max - min || 1;
  const lastX = data.length - 1 || 1;

  const px = (i) => (i / lastX) * 100;
  const py = (v) => height - ((v - min) / span) * height;
  const zeroY = py(0);

  const line = data.map((d, i) => `${i === 0 ? 'M' : 'L'}${px(i).toFixed(2)},${py(d.y).toFixed(2)}`).join(' ');
  const area = `${line} L100,${zeroY.toFixed(2)} L0,${zeroY.toFixed(2)} Z`;

  return (
    <figure className="chart" aria-label={label}>
      <svg viewBox={`0 0 100 ${height}`} preserveAspectRatio="none" className="chart-svg" style={{ height }}>
        <path d={area} className="chart-area" />
        <line x1="0" y1={zeroY} x2="100" y2={zeroY} className="chart-zero" vectorEffect="non-scaling-stroke" />
        <path d={line} className="chart-line" vectorEffect="non-scaling-stroke" />
        {markers.map((m) => (
          <line
            key={m.at}
            x1={px(m.at)}
            y1="0"
            x2={px(m.at)}
            y2={height}
            className="chart-marker"
            vectorEffect="non-scaling-stroke"
          />
        ))}
        {data.map((d, i) => (
          <rect key={d.x} x={px(i) - 50 / lastX} y="0" width={100 / lastX} height={height} fill="transparent">
            <title>{`${d.x}: ${valueLabel(d.y)}`}</title>
          </rect>
        ))}
      </svg>
      <figcaption className="chart-axis">
        <span>{data[0].x}</span>
        <span className="chart-axis-end">{data.at(-1).x}</span>
      </figcaption>
    </figure>
  );
}

/**
 * Two series on one timeline, drawn on a shared scale.
 *
 * Built for rolling returns, where a single median hides the thing that
 * matters: whether a fund was ahead of its category throughout, or only
 * during one stretch that happens to flatter the average.
 */
export function TwoLineChart({
  data,
  height = 150,
  label = 'Series',
  aLabel = 'This fund',
  bLabel = 'Category median',
  valueLabel = fmtPct
}) {
  if (!data?.length) return null;

  const values = data.flatMap((d) => [d.a, d.b]).filter((v) => v !== null && v !== undefined);
  if (!values.length) return null;
  const max = Math.max(...values, 0);
  const min = Math.min(...values, 0);
  const span = max - min || 1;
  const lastX = data.length - 1 || 1;

  const px = (i) => (i / lastX) * 100;
  const py = (v) => height - ((v - min) / span) * height;
  const zeroY = py(0);

  // Gaps are skipped rather than interpolated, so a missing month does not draw
  // a straight line through data that does not exist.
  const path = (key) => {
    let out = '';
    let pen = false;
    data.forEach((d, i) => {
      const v = d[key];
      if (v === null || v === undefined) {
        pen = false;
        return;
      }
      out += `${pen ? 'L' : 'M'}${px(i).toFixed(2)},${py(v).toFixed(2)} `;
      pen = true;
    });
    return out.trim();
  };

  return (
    <figure className="chart" aria-label={label}>
      <svg viewBox={`0 0 100 ${height}`} preserveAspectRatio="none" className="chart-svg" style={{ height }}>
        <line x1="0" y1={zeroY} x2="100" y2={zeroY} className="chart-zero" vectorEffect="non-scaling-stroke" />
        <path d={path('b')} className="chart-line chart-line-b" vectorEffect="non-scaling-stroke" />
        <path d={path('a')} className="chart-line" vectorEffect="non-scaling-stroke" />
        {data.map((d, i) => (
          <rect key={d.x} x={px(i) - 50 / lastX} y="0" width={100 / lastX} height={height} fill="transparent">
            <title>
              {`${d.x} — ${aLabel} ${valueLabel(d.a)}`}
              {d.b === null || d.b === undefined ? '' : `, ${bLabel} ${valueLabel(d.b)}`}
            </title>
          </rect>
        ))}
      </svg>
      <figcaption className="chart-axis">
        <span>{data[0].x}</span>
        <span className="chart-legend">
          <i className="chart-key" /> {aLabel}
          <i className="chart-key chart-key-b" /> {bLabel}
        </span>
        <span className="chart-axis-end">{data.at(-1).x}</span>
      </figcaption>
    </figure>
  );
}
